import { Column, Row } from "@silevis/reactgrid";
import { useCallback, useEffect, useState } from "react";
import { renameField } from "../../General/Helpers";
import { useDataChanged } from "../Contexts/DataChangedProvider";
import { useData } from "../Contexts/DataProvider";
import BaseTable from "./BaseTable";
import {
  columnWidth,
  DIGITS_ONLY,
  ftePriorityColumns,
  isDigitsOnly,
  parseNumber,
  renameHeaderRow,
} from "./TableHelpers";

const rowHeight = 35;

const sortColumns = (columns: Column[]): Column[] => {
  const isPriority = (col: Column) =>
    ftePriorityColumns.includes(String(col.columnId));
  return [
    ...columns.filter(isPriority),
    ...columns
      .filter((c) => !isPriority(c))
      .sort((a, b) => {
        const aId = String(a.columnId);
        const bId = String(b.columnId);

        if (isDigitsOnly(aId) && isDigitsOnly(bId)) {
          return Number(aId) - Number(bId);
        }
        return aId.localeCompare(bId);
      }),
  ];
};

const getColumns = (headCount: any[]) => {
  if (!headCount || !headCount.length) return [];
  const uniqueColumns = new Set<string>();
  headCount.forEach((item: any) => {
    Object.keys(item).forEach((key) => {
      if (
        key !== "new_headcountid" &&
        key !== "new_submitted" &&
        key !== "@odata.etag" &&
        key !== "_ownerid_value"
      )
        uniqueColumns.add(key);
    });
  });
  let columnsArray: Column[] = [];
  ftePriorityColumns.forEach((columnId) => {
    columnsArray.push({
      columnId,
      width: columnWidth(columnId),
      resizable: true,
    });
    uniqueColumns.delete(columnId);
  });
  uniqueColumns.forEach((key) =>
    columnsArray.push({
      columnId: key,
      width: 90,
      resizable: true,
    })
  );
  return sortColumns(columnsArray);
};

const calculateSummaries = (rows: any, columns: Column[]) => {
  const summaries: Record<string, number> = {};
  rows.forEach((row: any) => {
    columns.forEach((col) => {
      const val = row[col.columnId];
      if (typeof val === "number" && !isNaN(val)) {
        summaries[col.columnId] = (summaries[col.columnId] || 0) + val;
      }
    });
  });
  return summaries;
};

const getSummaryRow = (summaries: any, columns: Column[]) => {
  const cells = columns.map((col) => ({
    type: "text" as const,
    text:
      col.columnId === "new_codecompany"
        ? "Summary"
        : summaries[col.columnId]?.toLocaleString() ?? "",
    groupId: col.columnId,
    readOnly: true,
  }));
  return { rowId: "summary", height: rowHeight, cells };
};

const buildRows = (
  headCount: any[],
  columns: Column[],
  submitted: boolean
): Row[] => {
  const headerRow: any = renameHeaderRow(columns);
  const dataRows = headCount.map((row, idx) => ({
    rowId: idx,
    height: rowHeight,
    cells: columns.map((col) => {
      const value = row[col.columnId];
      const readOnly =
        submitted || ftePriorityColumns.includes(col.columnId.toString());
      if (
        typeof value === "number" ||
        renameField(String(col.columnId)).match(DIGITS_ONLY)
      ) {
        return {
          type: "number",
          value,
          groupId: col.columnId,
          readOnly,
        };
      } else if (typeof value === "string") {
        return {
          type: "text",
          text: value,
          groupId: col.columnId,
          readOnly,
        };
      } else {
        return {
          type: "text",
          text: "",
          groupId: col.columnId,
          readOnly,
        };
      }
    }),
    id: row.new_headcountid,
  }));

  const summaries = calculateSummaries(headCount, columns);
  const summaryRow = getSummaryRow(summaries, columns);

  return [headerRow, ...dataRows, summaryRow];
};

const FteTable = ({ submitted = false }) => {
  const { data, setData } = useData();
  const { setDataChanged } = useDataChanged();
  const headCount = data?.headCount ?? [];

  const [columns, setColumns] = useState<Column[]>(() => getColumns(headCount));
  const [rows, setRows] = useState<Row[]>(
    buildRows(headCount, columns, submitted)
  );

  useEffect(() => {
    if (data.headCount && data.headCount.length > 0) {
      const newCols = getColumns(headCount);
      setColumns((prev) => {
        const prevWidths = Object.fromEntries(
          prev.map((col) => [col.columnId, col.width])
        );
        return newCols.map((col) =>
          prevWidths[col.columnId]
            ? { ...col, width: prevWidths[col.columnId] }
            : col
        );
      });
      setRows(buildRows(data.headCount, columns, submitted));
    }
  }, [headCount.length, data]);

  const handleCellChange = useCallback(
    (changedCells: any) => {
      if (submitted) {
        return;
      }
      const newHeadCount = [...headCount];

      changedCells.forEach(({ rowId, columnId, newCell }: any) => {
        if (
          rowId === "header" ||
          rowId === "summary" ||
          !newCell ||
          newCell.readOnly
        )
          return;
        switch (newCell.type) {
          case "number":
            if (columnId in newHeadCount[rowId]) {
              (newHeadCount[rowId] as any)[columnId] = parseNumber(
                newCell.value
              );
            }
            break;
          case "text":
            if (columnId in newHeadCount[rowId]) {
              (newHeadCount[rowId] as any)[columnId] = newCell.text;
            }
            break;
        }
      });

      setData({
        ...data,
        headCount: newHeadCount,
      });
      setRows(buildRows(newHeadCount, columns, submitted));
      setDataChanged(true);
    },
    [headCount, setData, setDataChanged]
  );

  const modifyCellValues = (
    modifyBy: number,
    operation: "multiply" | "add" | "fixed",
    range: any[]
  ) => {
    if (
      !Array.isArray(range) ||
      range.length === 0 ||
      !range[0].columns ||
      !range[0].rows
    ) {
      return;
    }

    const newData = [...headCount];
    for (let column of range[0].columns) {
      for (let row of range[0].rows) {
        let cell = row.cells.find((c: any) => c.groupId === column.columnId);
        if (cell && cell.type === "number") {
          const { groupId, value } = cell;
          const { rowId } = row;
          switch (operation) {
            case "multiply":
              if (groupId in newData[rowId]) {
                (newData[rowId] as any)[groupId] = value * modifyBy;
              }
              break;
            case "add":
              if (groupId in newData[rowId]) {
                (newData[rowId] as any)[groupId] =
                  Number(cell.value ?? 0) + Number(modifyBy ?? 0);
              }
              break;
          }
        }
      }
    }
    setData({ ...data, headCount: newData });
    setRows(buildRows(newData, columns, submitted));
    setDataChanged(true);
  };

  return (
    <BaseTable
      columns={columns}
      setColumns={setColumns}
      rows={rows}
      stickyLeftColumns={2}
      onCellsChanged={handleCellChange}
      modifyCellValues={modifyCellValues}
    />
  );
};

export default FteTable;
